import {
  Metadata,
  MetadataProgram,
} from "@metaplex-foundation/mpl-token-metadata";
import * as anchor from "@project-serum/anchor";
import type { AugmentedProvider } from "@saberhq/solana-contrib";
import { TransactionEnvelope } from "@saberhq/solana-contrib";
import { getATAAddress, getOrCreateATA } from "@saberhq/token-utils";
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import type { Connection } from "@solana/web3.js";
import { Keypair, PublicKey } from "@solana/web3.js";
import invariant from "tiny-invariant";

import type { QuarrySDK } from "../..";
import type {
  ActivityMasterData,
  ActivityMetaData,
  ActivityProgram,
} from "../../programs/activity";
import {
  crmMint,
  mintWrapperProgram,
  transferWrapperProgram,
} from "./constant";
import { findActivityMasterAddress, findActivityMetadataAddress } from "./pda";
import type { PendingInitActivity, PendingMint } from "./types";

export class Activity {
  readonly program: ActivityProgram;

  constructor(readonly sdk: QuarrySDK) {
    this.program = sdk.programs.Activity;
  }

  get provider(): AugmentedProvider {
    return this.sdk.provider;
  }

  async init({
    authority,
    caffeineTokenMint,
    mintWrapper,
    minter,
    openRewardTimestamp,
    url,
    secondPartyNum,
    crmExchangeRate,
    crmPriceDecimal,
    multipliers,
  }: {
    authority: PublicKey;
    caffeineTokenMint: PublicKey;
    mintWrapper: PublicKey;
    minter: PublicKey;
    openRewardTimestamp: number;
    url: string;
    secondPartyNum: number;
    crmExchangeRate: number;
    crmPriceDecimal: number;
    multipliers: number[];
  }): Promise<PendingInitActivity> {
    const [activityMasterKey, bump] = await findActivityMasterAddress();
    console.log("activityMasterKey", activityMasterKey.toBase58());
    const tx = new TransactionEnvelope(this.provider, [
      this.program.instruction.initialize(
        bump,
        new anchor.BN(openRewardTimestamp),
        secondPartyNum,
        new anchor.BN(crmExchangeRate),
        crmPriceDecimal,
        url,
        multipliers,
        {
          accounts: {
            activityMaster: activityMasterKey,
            caffeineTokenMint: caffeineTokenMint,
            authority: authority,
            mintWrapper: mintWrapper,
            minter: minter,
            systemProgram: anchor.web3.SystemProgram.programId,
            rent: anchor.web3.SYSVAR_RENT_PUBKEY,
          },
        }
      ),
    ]);
    return { tx: tx, activityMaster: activityMasterKey, authority: authority };
  }

  async update({
    authority,
    timestampNow = null,
    url = null,
    op_second_party_num = null,
    op_crm_exchange_rate = null,
    op_crm_price_decimal = null,
    mintWrapper = null,
    minter = null,
  }: {
    authority: PublicKey;
    timestampNow: number | null;
    url: string | null;
    op_second_party_num: number | null;
    op_crm_exchange_rate: number | null;
    op_crm_price_decimal: number | null;
    mintWrapper: PublicKey | null;
    minter: PublicKey | null;
  }): Promise<TransactionEnvelope> {
    const [activityMasterKey, _] = await findActivityMasterAddress();
    let timestamp = null;
    if (timestampNow !== null) {
      timestamp = new anchor.BN(timestampNow);
    }
    const remainingAccounts: {
      pubkey: PublicKey;
      isWritable: boolean;
      isSigner: boolean;
    }[] = [];
    if (mintWrapper !== null && minter !== null) {
      remainingAccounts.push({
        pubkey: mintWrapper,
        isWritable: false,
        isSigner: false,
      });
      remainingAccounts.push({
        pubkey: minter,
        isWritable: true,
        isSigner: false,
      });
    }
    let exchange;
    if (op_crm_exchange_rate !== null) {
      exchange = new anchor.BN(op_crm_exchange_rate);
    } else {
      exchange = null;
    }
    const tx = new TransactionEnvelope(this.provider, [
      this.program.instruction.update(
        timestamp,
        url,
        op_second_party_num,
        exchange,
        op_crm_price_decimal,
        {
          accounts: {
            activityMaster: activityMasterKey,
            authority: authority,
          },
          remainingAccounts: remainingAccounts,
        }
      ),
    ]);
    return tx;
  }

  async mintCremaKey({
    user,
    degree,
  }: {
    user: PublicKey;
    degree: number;
  }): Promise<PendingMint> {
    const [activityMasterKey, _] = await findActivityMasterAddress();
    const master = await this.fetchActivitymaster(activityMasterKey);
    invariant(master !== null, "activity master is null");
    const mint = Keypair.generate();
    const receiverAtaAccount = await getATAAddress({
      mint: mint.publicKey,
      owner: user,
    });
    const caffeintTokenAccount = await getATAAddress({
      mint: master.caffeineTokenMint,
      owner: user,
    });
    const nftMetadata = await Metadata.getPDA(mint.publicKey);
    const [activityMetadata, abump] = await findActivityMetadataAddress(
      mint.publicKey
    );

    const tx = new TransactionEnvelope(
      this.provider,
      [
        this.program.instruction.mint(abump, degree, {
          accounts: {
            activityMaster: activityMasterKey,
            payerAccount: user,
            caffeineTokenMint: master.caffeineTokenMint,
            tokenAccount: caffeintTokenAccount,
            mint: mint.publicKey,
            receiverAtaAccount: receiverAtaAccount,
            metadata: nftMetadata,
            activityMetadata: activityMetadata,
            tokenMetadataProgram: MetadataProgram.PUBKEY,
            tokenProgram: TOKEN_PROGRAM_ID,
            associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
            systemProgram: anchor.web3.SystemProgram.programId,
            rent: anchor.web3.SYSVAR_RENT_PUBKEY,
          },
        }),
      ],
      [mint]
    );
    return {
      tx: tx,
      user: user,
      mint: mint.publicKey,
      receiverTokenAccount: receiverAtaAccount,
      metadata: nftMetadata,
      activityMetadata: activityMasterKey,
    };
  }

  async upgrade({
    user,
    mint,
  }: {
    user: PublicKey;
    mint: PublicKey;
  }): Promise<TransactionEnvelope> {
    const [activityMasterKey, _] = await findActivityMasterAddress();
    console.log("activityMasterKey", activityMasterKey.toBase58());
    const master = await this.fetchActivitymaster(activityMasterKey);
    invariant(master !== null, "activity master is null");
    const caffeintTokenAccount = await getATAAddress({
      mint: master.caffeineTokenMint,
      owner: user,
    });
    const nftTokenAccount = await getATAAddress({ mint: mint, owner: user });
    const [activityMetadata, __] = await findActivityMetadataAddress(mint);

    const tx = new TransactionEnvelope(this.provider, [
      this.program.instruction.upgradeNft({
        accounts: {
          activityMaster: activityMasterKey,
          payerAccount: user,
          caffeineTokenMint: master.caffeineTokenMint,
          tokenAccount: caffeintTokenAccount,
          mint: mint,
          nftAtaAccount: nftTokenAccount,
          activityMetadata: activityMetadata,
          tokenProgram: TOKEN_PROGRAM_ID,
        },
      }),
    ]);
    return tx;
  }

  async claimReward({
    user,
    mint,
  }: {
    user: PublicKey;
    mint: PublicKey;
  }): Promise<TransactionEnvelope> {
    const [activityMasterKey, _] = await findActivityMasterAddress();
    const master = await this.fetchActivitymaster(activityMasterKey);
    invariant(master !== null, "activity master is null");
    const nftMetadata = await Metadata.getPDA(mint);
    const [activityMetadata, __] = await findActivityMetadataAddress(mint);
    const receiverTokenAccount = await getATAAddress({
      mint: crmMint,
      owner: user,
    });
    const nftTokenAccount = await getATAAddress({ mint: mint, owner: user });
    const res = await getOrCreateATA({
      provider: this.provider,
      mint: crmMint,
    });
    console.log("activityMetadata", activityMetadata.toBase58());
    const tx = new TransactionEnvelope(this.provider, [
      this.program.instruction.claimReward({
        accounts: {
          activityMaster: activityMasterKey,
          payerAccount: user,
          mint: mint,
          nftAtaAccount: nftTokenAccount,
          metadata: nftMetadata,
          activityMetadata: activityMetadata,
          mintWrapper: master.mintWrapper,
          mintWrapperProgram: mintWrapperProgram,
          minter: master.minter,
          rewardsTokenMint: crmMint,
          rewardsTokenAccount: receiverTokenAccount,
          tokenProgram: TOKEN_PROGRAM_ID,
          recentBlockhashAccount: anchor.web3.SYSVAR_RECENT_BLOCKHASHES_PUBKEY,
          instructionSysvarAccount: anchor.web3.SYSVAR_INSTRUCTIONS_PUBKEY,
        },
      }),
    ]);
    if (res.instruction !== null) {
      tx.prepend(res.instruction);
    }
    return tx;
  }

  async claimSecondParty({
    user,
    mint,
    token_mint,
  }: {
    user: PublicKey;
    mint: PublicKey;
    token_mint: PublicKey;
  }): Promise<TransactionEnvelope | null> {
    const [activityMasterKey, _] = await findActivityMasterAddress();
    const master = await this.fetchActivitymaster(activityMasterKey);
    invariant(master !== null, "activity master is null");
    const nftMetadata = await Metadata.getPDA(mint);
    const [activityMetadata, __] = await findActivityMetadataAddress(mint);
    const nftTokenAccount = await getATAAddress({ mint: mint, owner: user });
    const receiverTokenAccount = await getATAAddress({
      mint: crmMint,
      owner: user,
    });
    const remainingAccounts: {
      pubkey: PublicKey;
      isWritable: boolean;
      isSigner: boolean;
    }[] = [];
    const res = await getOrCreateATA({
      provider: this.provider,
      mint: token_mint,
    });

    if (!token_mint.equals(crmMint)) {
      const transferInfoMap = await this.fetchTransferInfoMap();
      if (!transferInfoMap.mints.get(token_mint.toBase58())) {
        console.log("token mint not supported");
        return null;
      }
      const tokenWrapper = transferInfoMap.wrappers.get(token_mint.toBase58());
      invariant(tokenWrapper !== undefined, "tokenWrapper in undefined");
      const sourceTokenAccount = await getATAAddress({
        mint: token_mint,
        owner: tokenWrapper,
      });

      remainingAccounts.push({
        pubkey: token_mint,
        isWritable: false,
        isSigner: false,
      });
      remainingAccounts.push({
        pubkey: tokenWrapper,
        isWritable: true,
        isSigner: false,
      });
      const tokenTransferer = transferInfoMap.transferer.get(
        token_mint.toBase58()
      );
      invariant(tokenTransferer !== undefined, "tokenTransferer is undefined");
      remainingAccounts.push({
        pubkey: tokenTransferer,
        isWritable: true,
        isSigner: false,
      });
      remainingAccounts.push({
        pubkey: sourceTokenAccount,
        isWritable: true,
        isSigner: false,
      });
      remainingAccounts.push({
        pubkey: res.address,
        isWritable: true,
        isSigner: false,
      });
    }
    const tx = new TransactionEnvelope(this.provider, [
      this.program.instruction.claimSecondParty({
        accounts: {
          activityMaster: activityMasterKey,
          payerAccount: user,
          mint: mint,
          nftAtaAccount: nftTokenAccount,
          metadata: nftMetadata,
          activityMetadata: activityMetadata,
          tokenProgram: TOKEN_PROGRAM_ID,
          transferWrapperProgram: transferWrapperProgram,
          mintWrapper: master.mintWrapper,
          mintWrapperProgram: mintWrapperProgram,
          minter: master.minter,
          rewardsTokenMint: crmMint,
          rewardsTokenAccount: receiverTokenAccount,
        },
        remainingAccounts: remainingAccounts,
      }),
    ]);
    if (res.instruction !== null) {
      tx.prepend(res.instruction);
    }
    return tx;
  }

  async addTransfer({
    authority,
    wrapper,
    transferer,
    tokenMint,
    idx,
    exchange_rate,
    decimal,
    name,
  }: {
    authority: PublicKey;
    wrapper: PublicKey;
    transferer: PublicKey;
    tokenMint: PublicKey;
    idx: number;
    exchange_rate: number;
    decimal: number;
    name: string;
  }): Promise<TransactionEnvelope> {
    const [activityMasterKey, _] = await findActivityMasterAddress();
    const tx = new TransactionEnvelope(this.provider, [
      this.program.instruction.addTransferer(
        idx,
        new anchor.BN(exchange_rate),
        new anchor.BN(decimal),
        name,
        {
          accounts: {
            activityMaster: activityMasterKey,
            authority: authority,
          },
          remainingAccounts: [
            {
              pubkey: tokenMint,
              isWritable: false,
              isSigner: false,
            },
            {
              pubkey: wrapper,
              isWritable: false,
              isSigner: false,
            },
            {
              pubkey: transferer,
              isWritable: false,
              isSigner: false,
            },
          ],
        }
      ),
    ]);
    return tx;
  }

  async claimCaffeine({
    user,
    amount,
  }: {
    user: PublicKey;
    amount: number;
  }): Promise<TransactionEnvelope> {
    const [activityMasterKey, _] = await findActivityMasterAddress();
    const master = await this.fetchActivitymaster(activityMasterKey);
    invariant(master !== null, "master is null");
    const caffeineTokenAccount = await getATAAddress({
      mint: master.caffeineTokenMint,
      owner: user,
    });
    const res = await getOrCreateATA({
      provider: this.provider,
      mint: crmMint,
    });

    const tx = new TransactionEnvelope(this.provider, [
      this.program.instruction.claimCaffeine(new anchor.BN(amount), {
        accounts: {
          activityMaster: activityMasterKey,
          payerAccount: user,
          caffeineTokenMint: master.caffeineTokenMint,
          caffeineTokenAccount: caffeineTokenAccount,
          rewardsTokenMint: crmMint,
          rewardsTokenAccount: res.address,
          tokenProgram: TOKEN_PROGRAM_ID,
          mintWrapper: master.mintWrapper,
          mintWrapperProgram: mintWrapperProgram,
          minter: master.minter,
        },
      }),
    ]);
    if (res.instruction !== null) {
      tx.prepend(res.instruction);
    }
    return tx;
  }

  async close({
    authority,
  }: {
    authority: PublicKey;
  }): Promise<TransactionEnvelope> {
    const [activityMasterKey, _] = await findActivityMasterAddress();
    const tx = new TransactionEnvelope(this.provider, [
      this.program.instruction.closePdaAccount({
        accounts: {
          activityMaster: activityMasterKey,
          authority: authority,
        },
      }),
    ]);
    return tx;
  }

  async fetchCremaKeysV2(connection: Connection, user: PublicKey) {
    const [activityMasterKey, _] = await findActivityMasterAddress();
    const master = await this.fetchActivitymaster(activityMasterKey);
    invariant(master !== null, "master is null");
    const metadatas = await Metadata.findDataByOwner(connection, user);
    const mints = metadatas
      .filter(function (item) {
        const creators = item?.data?.creators;
        if (
          creators !== undefined &&
          creators !== null &&
          creators.length > 0
        ) {
          if (
            creators[0]?.address === activityMasterKey.toBase58() &&
            creators[0]?.verified
          ) {
            return item;
          }
        }
      })
      .map((m) => m.mint);
    const activityMetadataKeys: PublicKey[] = [];
    for (const mint of mints) {
      const mintKey = new PublicKey(mint);
      const metaKey = await findActivityMetadataAddress(mintKey);
      console.log(metaKey[0].toBase58());
      activityMetadataKeys.push(metaKey[0]);
    }
    const activitys = await this.program.account.activityMetadata.fetchMultiple(
      activityMetadataKeys
    );
    if (activitys.length === 0) {
      return {};
    }

    const names = new Map();
    const decimals = new Map();
    for (let idx = 0; idx < master.secondPartyNum; idx++) {
      names.set(
        master.transfers[idx]?.mint.toBase58(),
        master.transfers[idx]?.name
      );
      decimals.set(
        master.transfers[idx]?.mint.toBase58(),
        master.transfers[idx]?.decimals
      );
    }

    const result = [];
    for (const e of activitys) {
      if (e === null) {
        continue;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const activity: any = e;
      const isClaimed = new Map();
      const claimAmounts = new Map();
      isClaimed.set(
        crmMint.toBase58(),
        activity.isClaimed[activity.isClaimed.length - 1]
      );
      for (let idx = 0; idx < master.secondPartyNum; idx++) {
        isClaimed.set(
          master.transfers[idx]?.mint.toBase58(),
          activity.isClaimed[idx]
        );
        claimAmounts.set(
          master.transfers[idx]?.mint.toBase58(),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          activity.claimAmounts[idx].toNumber()
        );
      }
      result.push({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        mint: activity.mint.toBase58(),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        caffeine: activity.caffeine.toNumber(),
        degree: activity.degree,
        isCrmClaimed: activity.isCrmClaimed,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        crmClaimedAmount: activity.crmClaimedAmount.toNumber(),
        isSecondPartyClaimed: isClaimed,
        claimAmounts: claimAmounts,
      });
    }
    return {
      keys: result,
      meta: {
        names: names,
        decimals: decimals,
      },
    };
  }
  /**
   * Fetches Activity Master Info.
   * @returns
   */
  async fetchActivitymaster(
    master: PublicKey
  ): Promise<ActivityMasterData | null> {
    const accountInfo = await this.program.provider.connection.getAccountInfo(
      master
    );
    if (!accountInfo) {
      return null;
    }
    return this.program.coder.accounts.decode<ActivityMasterData>(
      "ActivityMaster",
      accountInfo.data
    );
  }

  /**
   * Fetches Activity Metadata Info.
   * @param activityMetadata
   * @returns
   */
  async fetchMetadata(
    activityMetadata: PublicKey
  ): Promise<ActivityMetaData | null> {
    const accountInfo = await this.program.provider.connection.getAccountInfo(
      activityMetadata
    );
    if (!accountInfo) {
      return null;
    }
    return this.program.coder.accounts.decode<ActivityMetaData>(
      "ActivityMetadata",
      accountInfo.data
    );
  }
  /**
   *
   *
   */
  async fetchTransferInfoMap(): Promise<{
    mints: Map<string, PublicKey>;
    names: Map<string, string>;
    decimals: Map<string, number>;
    wrappers: Map<string, PublicKey>;
    transferer: Map<string, PublicKey>;
    exchangeRate: Map<string, number>;
    priceDecimal: Map<string, number>;
  }> {
    const [activityMasterKey, _] = await findActivityMasterAddress();
    const master = await this.fetchActivitymaster(activityMasterKey);
    const names = new Map();
    const decimals = new Map();
    const wrappers = new Map();
    const transferers = new Map();
    const mints = new Map();
    const exchangeRates = new Map();
    const priceDecimals = new Map();
    invariant(master !== null, "master account is null");
    for (let idx = 0; idx < master.secondPartyNum; idx++) {
      invariant(master.transfers !== undefined);
      mints.set(
        master.transfers[idx]?.mint.toBase58(),
        master.transfers[idx]?.mint
      );
      names.set(
        master.transfers[idx]?.mint.toBase58(),
        master.transfers[idx]?.name
      );
      decimals.set(
        master.transfers[idx]?.mint.toBase58(),
        master.transfers[idx]?.decimals
      );
      wrappers.set(
        master.transfers[idx]?.mint.toBase58(),
        master.transfers[idx]?.transferWrapper
      );
      transferers.set(
        master.transfers[idx]?.mint.toBase58(),
        master.transfers[idx]?.transferer
      );
      exchangeRates.set(
        master.transfers[idx]?.mint.toBase58(),
        master.transfers[idx]?.exchangeRate
      );
      exchangeRates.set(
        master.transfers[idx]?.mint.toBase58(),
        master.transfers[idx]?.priceDecimal
      );
    }
    return {
      mints: mints,
      names: names,
      decimals: decimals,
      wrappers: wrappers,
      transferer: transferers,
      exchangeRate: exchangeRates,
      priceDecimal: priceDecimals,
    };
  }
}
